import React, { FC } from "react";
import styles from "./FilterDropDown.module.scss";
import { set } from "lodash";
import { useLocation } from "react-router-dom";
import { SearchIcon } from "customIcons";
import { CloseIcon } from "@features/common/SvgIcons";

// ============================================================================
// Types & Interfaces
// ============================================================================
interface FilterOption {
  label: string;
  value: string;
}

interface FilterDropDownProps {
  options?: FilterOption[];
  selectedValue?: string | null;
  onSelect?: (value: string | null, name?: string) => void;
  setIsOpen?: (isOpen: boolean) => void;
  onPromoSearchChange?: (val: string) => void;
  promoSearchTerm?: string;
  nextPageUrl?: string | null;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}

const FilterDropDown: FC<FilterDropDownProps> = ({
  options,
  selectedValue,
  onSelect,
  setIsOpen,
  onPromoSearchChange,
  promoSearchTerm,
  nextPageUrl,
  isFetchingNextPage,
  onLoadMore,
}) => {
  const { pathname } = useLocation();
  const isOnSendReminderPage = pathname.includes("send-reminder");
  const observerTarget = React.useRef<HTMLDivElement>(null);

  // Infinite scroll implementation
  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && nextPageUrl && !isFetchingNextPage) {
          onLoadMore?.();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [nextPageUrl, isFetchingNextPage, onLoadMore]);
  // ============================================================================
  // Event Handlers
  // ============================================================================
  /**
   * Handles option click event
   * Toggles selection: deselects if same option clicked, otherwise selects new option
   *
   * @param {string} value - The value of the clicked option
   */
  const handleOptionClick = (value: string, name?: string) => {
    // If the same option is clicked, deselect it (set to null)
    if (selectedValue === value) {
      onSelect?.(null);
      setIsOpen?.(false);
    } else {
      onSelect?.(value, name);
      setIsOpen?.(false);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div
      className={styles["custom-dropdown"]}
      style={{
        width: isOnSendReminderPage ? "228px" : "",
        maxHeight: "300px",
        overflowY: "auto",
      }}
    >
      {isOnSendReminderPage && (
        <div
          className={styles["custom-dropdown__search-bar"]}
          style={{ width: isOnSendReminderPage ? "198px" : "" }}
        >
          <SearchIcon />
          <input
            type="text"
            placeholder="Search by Promotion Name"
            className={styles["custom-dropdown__search-bar__input"]}
            value={promoSearchTerm}
            onChange={(e) => onPromoSearchChange?.(e.target.value)}
          />
          {promoSearchTerm && (
            <div
              className={styles["custom-dropdown__search-bar__close-icon"]}
              onClick={() => onPromoSearchChange?.("")}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                width: "20px",
              }}
            >
              <CloseIcon />
            </div>
          )}
        </div>
      )}
      {options?.map((option, index) => (
        <div
          key={index}
          className={`${styles["custom-dropdown__option"]} ${
            selectedValue === option.value
              ? styles["custom-dropdown__option--selected"]
              : ""
          }`}
          onClick={() => handleOptionClick(option.value,option?.label)}
        >
          {option?.label}
        </div>
      ))}
      {/* Intersection observer target for infinite scroll */}
      <div ref={observerTarget} style={{ height: "1px" }} />
      {/* Loading indicator */}
      {isFetchingNextPage && (
        <div
          style={{
            padding: "10px",
            textAlign: "center",
            color: "#868785",
            fontSize: "14px",
          }}
        >
          Loading more...
        </div>
      )}
    </div>
  );
};

export default FilterDropDown;
